/**
 * GRANULLO PLATFORM EVOLUTION — System Stress Context
 * Phase 4: Monitoring + Exposure + Cascade Integration
 * 
 * FOUNDER DIRECTIVE:
 * Begin canonical plumbing for System Stress as an engine-derived metric.
 * Do not build it as a cosmetic score.
 * 
 * System Stress must derive from combinations of:
 * - signal drift density
 * - unresolved decision backlog
 * - exposure intensity
 * - cascade risk
 * - authority response lag
 * - commit pressure
 * - monitoring burden
 * 
 * ARCHITECTURE CONSTRAINT: Must derive from canonical engine outputs only.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// SYSTEM STRESS TYPES
// ============================================================================

export const STRESS_LEVELS = {
  MINIMAL: 'minimal',       // 0-20
  LOW: 'low',               // 21-40
  MODERATE: 'moderate',     // 41-60
  HIGH: 'high',             // 61-80
  CRITICAL: 'critical'      // 81-100
};

export const STRESS_FACTORS = {
  SIGNAL_DRIFT: 'signal_drift',
  DECISION_BACKLOG: 'decision_backlog',
  EXPOSURE_INTENSITY: 'exposure_intensity',
  CASCADE_RISK: 'cascade_risk',
  AUTHORITY_LAG: 'authority_lag',
  COMMIT_PRESSURE: 'commit_pressure',
  MONITORING_BURDEN: 'monitoring_burden'
};

// Factor weights for stress calculation
const FACTOR_WEIGHTS = {
  [STRESS_FACTORS.SIGNAL_DRIFT]: 0.20,
  [STRESS_FACTORS.DECISION_BACKLOG]: 0.15,
  [STRESS_FACTORS.EXPOSURE_INTENSITY]: 0.20,
  [STRESS_FACTORS.CASCADE_RISK]: 0.15,
  [STRESS_FACTORS.AUTHORITY_LAG]: 0.10,
  [STRESS_FACTORS.COMMIT_PRESSURE]: 0.10,
  [STRESS_FACTORS.MONITORING_BURDEN]: 0.10
};

// ============================================================================
// SYSTEM STRESS CALCULATION
// ============================================================================

/**
 * Calculate individual stress factor
 */
export function calculateStressFactor(factor, data) {
  switch (factor) {
    case STRESS_FACTORS.SIGNAL_DRIFT:
      // Based on drift severity and count
      return calculateSignalDriftStress(data);
    
    case STRESS_FACTORS.DECISION_BACKLOG:
      // Based on unresolved decisions
      return calculateDecisionBacklogStress(data);
    
    case STRESS_FACTORS.EXPOSURE_INTENSITY:
      // Based on exposure levels
      return calculateExposureStress(data);
    
    case STRESS_FACTORS.CASCADE_RISK:
      // Based on cascade analysis
      return calculateCascadeStress(data);
    
    case STRESS_FACTORS.AUTHORITY_LAG:
      // Based on response times
      return calculateAuthorityLagStress(data);
    
    case STRESS_FACTORS.COMMIT_PRESSURE:
      // Based on commit consumption
      return calculateCommitPressureStress(data);
    
    case STRESS_FACTORS.MONITORING_BURDEN:
      // Based on monitoring state
      return calculateMonitoringBurdenStress(data);
    
    default:
      return { score: 0, contribution: 0, description: 'Unknown factor' };
  }
}

function calculateSignalDriftStress(data) {
  const { driftSeverity, totalSignals = 1 } = data;
  if (!driftSeverity) return { score: 0, contribution: 0, description: 'No drift data' };
  
  const { breakdown = {} } = driftSeverity;
  const severityScore = (breakdown.catastrophic || 0) * 100 + 
                       (breakdown.emergency || 0) * 75 + 
                       (breakdown.critical || 0) * 50 + 
                       (breakdown.warning || 0) * 25;
  
  const normalizedScore = Math.min(100, (severityScore / totalSignals) * 2);
  
  return {
    score: Math.round(normalizedScore),
    contribution: Math.round(normalizedScore * FACTOR_WEIGHTS[STRESS_FACTORS.SIGNAL_DRIFT]),
    description: `${driftSeverity.driftingSignals || 0} signals drifting`
  };
}

function calculateDecisionBacklogStress(data) {
  const { pending = 0, escalated = 0, overdue = 0 } = data;
  
  // Overdue decisions are most stressful
  const score = Math.min(100, pending * 5 + escalated * 15 + overdue * 30);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.DECISION_BACKLOG]),
    description: `${overdue} overdue, ${pending} pending decisions`
  };
}

function calculateExposureStress(data) {
  const { exposureScore = 0, criticalExposures = 0 } = data;
  
  const score = Math.min(100, exposureScore + criticalExposures * 20);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.EXPOSURE_INTENSITY]),
    description: `Exposure score: ${exposureScore}`
  };
}

function calculateCascadeStress(data) {
  const { systemRisk = 0, vulnerableNodes = 0 } = data;
  
  const score = Math.min(100, systemRisk + vulnerableNodes * 5);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.CASCADE_RISK]),
    description: `System risk: ${systemRisk}%, ${vulnerableNodes} vulnerable nodes`
  };
}

function calculateAuthorityLagStress(data) {
  const { avgResponseTime = 0, overdueCount = 0 } = data;
  
  // Response time in hours, stress increases as it approaches deadline
  const timeScore = Math.min(50, avgResponseTime * 2);
  const overdueScore = overdueCount * 25;
  const score = Math.min(100, timeScore + overdueScore);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.AUTHORITY_LAG]),
    description: `${overdueCount} overdue responses`
  };
}

function calculateCommitPressureStress(data) {
  const { consumedPercent = 0, burnRate = 0 } = data;
  
  // Stress increases as commits are consumed
  let score = 0;
  if (consumedPercent >= 90) score = 100;
  else if (consumedPercent >= 75) score = 70;
  else if (consumedPercent >= 50) score = 40;
  else score = consumedPercent * 0.5;
  
  // Add burn rate factor
  score = Math.min(100, score + burnRate * 10);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.COMMIT_PRESSURE]),
    description: `${consumedPercent}% commits consumed`
  };
}

function calculateMonitoringBurdenStress(data) {
  const { offlineSignals = 0, alertBacklog = 0, totalSignals = 1 } = data;
  
  const offlinePercent = (offlineSignals / totalSignals) * 100;
  const score = Math.min(100, offlinePercent + alertBacklog * 5);
  
  return {
    score: Math.round(score),
    contribution: Math.round(score * FACTOR_WEIGHTS[STRESS_FACTORS.MONITORING_BURDEN]),
    description: `${offlineSignals} offline, ${alertBacklog} alerts`
  };
}

/**
 * Calculate overall System Stress Index
 */
export function calculateSystemStressIndex(factorData) {
  const factors = {};
  let totalScore = 0;
  
  Object.values(STRESS_FACTORS).forEach(factor => {
    const result = calculateStressFactor(factor, factorData[factor] || {});
    factors[factor] = result;
    totalScore += result.contribution;
  });
  
  // Determine stress level
  let level = STRESS_LEVELS.MINIMAL;
  if (totalScore >= 80) level = STRESS_LEVELS.CRITICAL;
  else if (totalScore >= 60) level = STRESS_LEVELS.HIGH;
  else if (totalScore >= 40) level = STRESS_LEVELS.MODERATE;
  else if (totalScore >= 20) level = STRESS_LEVELS.LOW;
  
  // Find primary stress contributor
  const primaryFactor = Object.entries(factors)
    .sort((a, b) => b[1].contribution - a[1].contribution)[0];
  
  return {
    index: Math.round(totalScore),
    level,
    factors,
    primaryFactor: primaryFactor ? { name: primaryFactor[0], ...primaryFactor[1] } : null,
    calculatedAt: new Date().toISOString()
  };
}

// ============================================================================
// CONTEXT
// ============================================================================

const SystemStressContext = createContext(null);

export function SystemStressProvider({ children }) {
  // Stress state
  const [stressIndex, setStressIndex] = useState({
    index: 0,
    level: STRESS_LEVELS.MINIMAL,
    factors: {},
    primaryFactor: null,
    calculatedAt: null
  });
  
  // Historical stress values for trend
  const [stressHistory, setStressHistory] = useState([]);
  
  // Factor data sources (set by integration)
  const [factorData, setFactorData] = useState({
    [STRESS_FACTORS.SIGNAL_DRIFT]: {},
    [STRESS_FACTORS.DECISION_BACKLOG]: {},
    [STRESS_FACTORS.EXPOSURE_INTENSITY]: {},
    [STRESS_FACTORS.CASCADE_RISK]: {},
    [STRESS_FACTORS.AUTHORITY_LAG]: {},
    [STRESS_FACTORS.COMMIT_PRESSURE]: {},
    [STRESS_FACTORS.MONITORING_BURDEN]: {}
  });
  
  /**
   * Update factor data
   */
  const updateFactorData = useCallback((factor, data) => {
    setFactorData(prev => ({
      ...prev,
      [factor]: { ...prev[factor], ...data }
    }));
  }, []);
  
  /**
   * Recalculate stress index
   */
  const recalculateStress = useCallback(() => {
    const newIndex = calculateSystemStressIndex(factorData);
    setStressIndex(newIndex);
    
    // Add to history (keep last 100 entries)
    setStressHistory(prev => [
      ...prev.slice(-99),
      { index: newIndex.index, timestamp: newIndex.calculatedAt }
    ]);
    
    return newIndex;
  }, [factorData]);
  
  /**
   * Batch update all factor data and recalculate
   */
  const updateAllFactors = useCallback((allFactorData) => {
    setFactorData(allFactorData);
    const newIndex = calculateSystemStressIndex(allFactorData);
    setStressIndex(newIndex);
    
    setStressHistory(prev => [
      ...prev.slice(-99),
      { index: newIndex.index, timestamp: newIndex.calculatedAt }
    ]);
    
    return newIndex;
  }, []);
  
  // Computed values
  const stressTrend = useMemo(() => {
    if (stressHistory.length < 2) return 'stable';
    
    const recent = stressHistory.slice(-5);
    const older = stressHistory.slice(-10, -5);
    
    if (older.length === 0) return 'stable';
    
    const recentAvg = recent.reduce((sum, h) => sum + h.index, 0) / recent.length;
    const olderAvg = older.reduce((sum, h) => sum + h.index, 0) / older.length;
    
    const change = recentAvg - olderAvg;
    if (change > 5) return 'increasing';
    if (change < -5) return 'decreasing';
    return 'stable';
  }, [stressHistory]);
  
  const stressSummary = useMemo(() => ({
    index: stressIndex.index,
    level: stressIndex.level,
    trend: stressTrend,
    primaryFactor: stressIndex.primaryFactor?.name || null,
    primaryContribution: stressIndex.primaryFactor?.contribution || 0,
    lastCalculated: stressIndex.calculatedAt,
    requiresAttention: stressIndex.level === STRESS_LEVELS.HIGH || stressIndex.level === STRESS_LEVELS.CRITICAL
  }), [stressIndex, stressTrend]);
  
  const value = {
    // Constants
    STRESS_LEVELS,
    STRESS_FACTORS,
    
    // State
    stressIndex,
    stressHistory,
    factorData,
    
    // Computed
    stressSummary,
    stressTrend,
    
    // Actions
    updateFactorData,
    recalculateStress,
    updateAllFactors,
    
    // Utility
    calculateSystemStressIndex,
    calculateStressFactor
  };
  
  return (
    <SystemStressContext.Provider value={value}>
      {children}
    </SystemStressContext.Provider>
  );
}

export function useSystemStress() {
  const context = useContext(SystemStressContext);
  if (!context) {
    throw new Error('useSystemStress must be used within SystemStressProvider');
  }
  return context;
}

export default SystemStressContext;
